import { CustomBuydownHistoryEntry, CustomBuydownHistoryTermRate } from '@src/types/CustomBuydown'

/**
 * Plans are stored in UTC and every time the merchant sees is converted to the Eastern clock, whatever
 * their own location: the activation instant drives the rates, so everyone must read the same clock.
 */
export const PLAN_TIME_ZONE = 'America/Toronto'

const easternParts = (date: string) =>
  Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: PLAN_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date(date))
      .map((part) => [part.type, part.value]),
  )

const pad = (value: number) => String(value).padStart(2, '0')

/** `2026-10-02` — the Eastern calendar date. */
export const formatEasternDate = (date: string) => {
  const { year, month, day } = easternParts(date)
  return `${year}-${month}-${day}`
}

/**
 * `2026-07-24 10:42 EST (14:42 UTC)` — the Eastern date and time under the product's fixed label
 * (`HNE` in French), with the stored UTC clock beside it.
 */
export const formatEasternDateTime = (date: string, language: string) => {
  const { year, month, day, hour, minute } = easternParts(date)
  const utc = new Date(date)
  const label = language.startsWith('fr') ? 'HNE' : 'EST'
  return `${year}-${month}-${day} ${hour}:${minute} ${label} (${pad(utc.getUTCHours())}:${pad(utc.getUTCMinutes())} UTC)`
}

/** The merchant's own inputs. */
export type PlanField = 'contribution' | 'adminFee' | 'maxFinancingAmount'

export type PlanChange =
  | { kind: 'field'; field: PlanField; from: number | null; to: number | null }
  | { kind: 'rate'; termMonths: number; from: number | null; to: number | null }

const FIELDS: { field: PlanField; read: (entry: CustomBuydownHistoryEntry) => number | null }[] = [
  { field: 'contribution', read: (entry) => entry.merchantFeeRate },
  { field: 'adminFee', read: (entry) => entry.borrowerFeeRate },
  { field: 'maxFinancingAmount', read: (entry) => entry.maxFinancingAmount },
]

const rateFor = (rates: CustomBuydownHistoryTermRate[], termMonths: number) =>
  rates.find((rate) => rate.termMonths === termMonths)?.promoRate ?? null

/**
 * What a revision changed compared to the one saved before it: the plan's inputs first, then the
 * customer rate of every term that moved. Dates are left out — each save restarts them.
 */
export const diffRevisions = (entry: CustomBuydownHistoryEntry, previous: CustomBuydownHistoryEntry): PlanChange[] => {
  const fieldChanges = FIELDS.flatMap<PlanChange>(({ field, read }) =>
    read(entry) === read(previous) ? [] : [{ kind: 'field', field, from: read(previous), to: read(entry) }],
  )

  const terms = [...new Set([...entry.promoTermRates, ...previous.promoTermRates].map((rate) => rate.termMonths))]
  const rateChanges = terms
    .sort((a, b) => a - b)
    .flatMap<PlanChange>((termMonths) => {
      const from = rateFor(previous.promoTermRates, termMonths)
      const to = rateFor(entry.promoTermRates, termMonths)
      return from === to ? [] : [{ kind: 'rate', termMonths, from, to }]
    })

  return [...fieldChanges, ...rateChanges]
}

/**
 * The rates moved with no listed cause: iFinance recalculated them, from its rate table or from the rate
 * rebate it applies. The rebate itself is never shown to the merchant, so the caption stays generic.
 */
export const isRatesOnlyChange = (changes: PlanChange[]) =>
  changes.length > 0 && changes.every((change) => change.kind === 'rate')

export type RevisionAuthor = { kind: 'financingCompany' } | { kind: 'headOffice' } | { kind: 'person'; name: string }

/**
 * Who the merchant sees as the author of a revision: the financing company for anything saved from
 * Merchants (or by an iFinance employee on the dashboard), the Head Office for a plan copied from the
 * banner, and the person otherwise.
 */
export const getRevisionAuthor = (entry: CustomBuydownHistoryEntry): RevisionAuthor => {
  switch (entry.updatedBySurface) {
    case 'IFinance':
      return { kind: 'financingCompany' }
    case 'Banner':
      return { kind: 'headOffice' }
    default:
      return { kind: 'person', name: entry.modifiedByUserFullname }
  }
}
